import { Mail, Phone, MapPin, ShieldCheck, Sparkles } from 'lucide-react';
import ContactForm from '../../components/ContactForm';
import SEO from '../../components/SEO';

export default function KontaktEn() {
  const kontaktSchema = {
    '@context': 'https://schema.org',
    '@type': 'ContactPage',
    name: 'Contact Danshaker',
    description: 'Contact Danshaker for advice, product inquiries, or free demo booking.',
    url: 'https://danshaker.dk/en/contact',
  };

  return (
    <div className="relative min-h-screen bg-slate-50 text-slate-900 antialiased">
      <SEO
        title="Contact Us - Danshaker ApS"
        description="Contact Danshaker in Randers, Denmark. Call +45 2530 3589 or email info@danshaker.dk for expert advice or free demonstration booking."
        path="/en/contact"
        lang="en"
        schema={kontaktSchema}
      />

      {/* Background gradients */}
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(14,103,162,0.05),transparent_60%)]" />

      {/* Main Content */}
      <section className="mx-auto max-w-7xl px-4 pt-16 pb-24 md:px-8 md:pt-24">
        <div className="grid gap-12 lg:grid-cols-12 lg:items-start">
          {/* Left Column: Contact Info */}
          <div className="lg:col-span-5 space-y-8 lg:sticky lg:top-40">
            <div className="space-y-4">
              <div className="inline-flex items-center gap-2 rounded-full bg-[#0E67A2]/10 px-4 py-1.5 text-xs font-bold text-[#0E67A2]">
                <Sparkles className="h-3.5 w-3.5" aria-hidden="true" />
                <span>We are here to assist you</span>
              </div>
              <h1 className="text-4xl font-extrabold tracking-tight text-slate-900 sm:text-5xl">
                Contact Danshaker
              </h1>
              <p className="text-lg text-slate-600 leading-relaxed">
                Do you have questions about the sorting machine, modular accessories, leasing, or would you like to schedule a free demo? Do not hesitate to contact us.
              </p>
            </div>

            {/* Contact Details Card */}
            <div className="rounded-2xl border border-slate-200 bg-white p-6 md:p-8 space-y-6 shadow-xl">
              <ul className="space-y-4 text-slate-700">
                <li className="flex items-start gap-4">
                  <MapPin className="h-6 w-6 text-[#0E67A2] shrink-0 mt-0.5" aria-hidden="true" />
                  <div>
                    <span className="block text-xs text-slate-500 font-bold uppercase tracking-wider">Address</span>
                    <span className="font-semibold text-slate-900">
                      Tebbestrupvej 72<br />
                      8940 Randers SV, Denmark
                    </span>
                  </div>
                </li>

                <li className="flex items-start gap-4">
                  <Phone className="h-6 w-6 text-[#0E67A2] shrink-0 mt-0.5" aria-hidden="true" />
                  <div>
                    <span className="block text-xs text-slate-500 font-bold uppercase tracking-wider">Phone</span>
                    <a
                      href="tel:25303589"
                      className="font-bold text-slate-900 hover:text-[#0E67A2] transition text-lg"
                    >
                      +45 2530 3589
                    </a>
                    <p className="text-xs text-slate-400 font-medium mt-0.5">Direct line to Rasmus</p>
                  </div>
                </li>

                <li className="flex items-start gap-4">
                  <Mail className="h-6 w-6 text-[#0E67A2] shrink-0 mt-0.5" aria-hidden="true" />
                  <div>
                    <span className="block text-xs text-slate-500 font-bold uppercase tracking-wider">Email</span>
                    <a
                      href="mailto:info@danshaker.dk"
                      className="font-bold text-slate-900 hover:text-[#0E67A2] transition break-all"
                    >
                      info@danshaker.dk
                    </a>
                  </div>
                </li>

                <li className="flex items-start gap-4 border-t border-slate-100 pt-4">
                  <ShieldCheck className="h-6 w-6 text-[#0E67A2] shrink-0 mt-0.5" aria-hidden="true" />
                  <div>
                    <span className="block text-xs text-slate-500 font-bold uppercase tracking-wider">Company Info</span>
                    <span className="font-semibold text-slate-900">
                      Danshaker ApS<br />
                      CVR / VAT: 45041071
                    </span>
                  </div>
                </li>
              </ul>
            </div>

            {/* Image of Rasmus */}
            <div className="relative overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-md">
              <img
                src="https://assets.auxodev.dk/cdn-cgi/image/width=600,quality=85,format=auto/assets/e0cd175c-a113-44c2-9ab9-d86f3c298af2/f33352be-916e-41a6-8683-b6a80cadb435.jpg"
                alt="Rasmus Gangsted-Rasmussen - Owner of Danshaker"
                width="600"
                height="400"
                loading="lazy"
                decoding="async"
                className="w-full h-auto block"
              />
              <div className="p-4 text-center">
                <p className="font-bold text-slate-900">Rasmus Gangsted-Rasmussen</p>
                <p className="text-xs text-slate-500">Owner</p>
              </div>
            </div>
          </div>

          {/* Right Column: Contact Form */}
          <div className="lg:col-span-7">
            <div className="rounded-3xl border border-slate-200 bg-white p-6 md:p-10 shadow-2xl">
              <div className="mb-8">
                <h2 className="text-2xl font-bold text-slate-900">Send us a message</h2>
                <p className="mt-1 text-sm text-slate-500 font-medium">
                  Fill in the form below and we will get back to you as soon as possible.
                </p>
              </div>

              <ContactForm source="Kontakt side" lang="en" />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
